import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import BookmarkSchema from "marketplace/bookmarks/_schema/bookmark-schema";
import { z } from "zod";

export type BookmarkedCompanyJSON = z.infer<typeof BookmarkedCompanySchema.BookmarkedCompanyJSONZ>;
export type BookmarkedCompanyParams = z.infer<typeof BookmarkedCompanySchema.BookmarkedCompanyZ>;

export default class BookmarkedCompanySchema {
  public static readonly BookmarkedCompanyJSONZ = z.object({
    ...BookmarkSchema.BookmarkJSONZ.shape,
    companyId: z.uuid(),
  });
  public static readonly BookmarkedCompanyZ = z.object({
    ...BookmarkSchema.BookmarkZ.shape,
    companyId: z.instanceof(Guid),
  });

  public static validate(values: unknown): BookmarkedCompanyParams {
    return parseOrThrow(BookmarkedCompanySchema.BookmarkedCompanyZ, values, {
      logError: true,
      schemaName: "BookmarkedCompanySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): BookmarkedCompanyJSON {
    return parseOrThrow(BookmarkedCompanySchema.BookmarkedCompanyJSONZ, values, {
      logError: true,
      schemaName: "BookmarkedCompanySchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static validateJSONArray(values: unknown): BookmarkedCompanyJSON[] {
    return parseOrThrow(z.array(BookmarkedCompanySchema.BookmarkedCompanyJSONZ), values, {
      logError: true,
      schemaName: "BookmarkedCompanySchema",
      operation: "validateJSONArray",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is BookmarkedCompanyParams {
    try {
      BookmarkedCompanySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is BookmarkedCompanyJSON {
    try {
      BookmarkedCompanySchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: BookmarkedCompanyJSON): BookmarkedCompanyParams {
    const parsed = parseOrThrow(BookmarkedCompanySchema.BookmarkedCompanyJSONZ, data, {
      logError: true,
      schemaName: "BookmarkedCompanySchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return BookmarkedCompanySchema.codec.decode(parsed);
  }

  public static deserializeArray(data: BookmarkedCompanyJSON[]): BookmarkedCompanyParams[] {
    return data.map((item) => BookmarkedCompanySchema.deserialize(item));
  }

  public static serialize(document: BookmarkedCompanyParams): BookmarkedCompanyJSON {
    const parsed = BookmarkedCompanySchema.validate(document);
    return BookmarkedCompanySchema.codec.encode(parsed);
  }

  public static serializeArray(documents: BookmarkedCompanyParams[]): BookmarkedCompanyJSON[] {
    return documents.map((item) => BookmarkedCompanySchema.serialize(item));
  }

  private static readonly codec = z.codec(
    BookmarkedCompanySchema.BookmarkedCompanyJSONZ,
    BookmarkedCompanySchema.BookmarkedCompanyZ,
    {
      decode: (data) => ({
        ...BookmarkSchema.deserialize(data),
        companyId: Guid.fromJSON(data.companyId)!,
      }),
      encode: (bookmark) => ({
        ...BookmarkSchema.serialize(bookmark),
        companyId: bookmark.companyId.toJSON(),
      }),
    }
  );
}
